import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import Chatbox from './components/Chatbox';
import ChatInput from './components/ChatInput';
import { useTheme } from "../../../context/ThemeContext";
import { useNavigate } from 'react-router-dom';
import {
  fetchUserChats,
  fetchChatMessages,
  fetchAIResponse,
  createChat,
  renameChatAPI,
  deleteChatAPI,
  suggestTitle,
  storeMessageToDB,
  fetchChatModel,
  checkRemainingTokens
}
  from '../../api';
import { useAuth } from "../../../context/AuthContext";
import { encrypt, decrypt } from '../../../encryption';
import CustomToaster from './components/CustomToaster';

function ChatPage() {
  // State for chat functionality
  const [isOpen, setIsOpen] = useState(true);
  const [model, setModel] = useState(1);
  const [conversations, setConversations] = useState([]);
  const [currentConversationIndex, setCurrentConversationIndex] = useState(null);
  const { user, isLoggedIn } = useAuth();
  const [loading, setLoading] = useState(false); // Track loading status
  const [chatId, setChatId] = useState(null);
  const [globalUserName, setGlobalUserName] = useState("-");
  const [globalEmail, setGlobalEmail] = useState("-");
  const [lastBotResponse, setLastBotResponse] = useState('');
  const [activeResponseChatId, setActiveResponseChatId] = useState(null);
  const [checkRemainingAITokens, setcheckRemainingAITokens] = useState(true);
  const [isWebSearch, setIsWebSearch] = useState(false);
  const navigate = useNavigate();
  const { isDarkTheme } = useTheme();

  // Toggles the sidebar
  const toggleSidebar = () => setIsOpen(!isOpen);

  const fetchChats = async () => {
    try {
      const response = await fetchUserChats(user);
      setConversations(response.chats.map((chat) => ({ ...chat, messages: [] })));
      const result = await checkRemainingTokens();
      setcheckRemainingAITokens(result.can_generate);
    } catch (error) {
      console.error('Error fetching user chats:', error);
    }
  };

  // Fetch user chats on component mount
  useEffect(() => {
    if (isLoggedIn && user) {
      fetchChats();
    }
  }, [isLoggedIn, user]);

  // Start a new chat
  const startNewChat = async () => {
    if (!isLoggedIn || !user) {
      console.error('User not logged in');
      return;
    }

    try {
      const response = await createChat(user, `Conversation ${conversations.length + 1}`, model)

      const newChat = {
        id: response.chat_id,
        title: `Conversation ${conversations.length + 1}`,
        messages: [],
      };
      setChatId(newChat.id)
      setConversations([newChat, ...conversations]);
      setCurrentConversationIndex(0);
      // Update the URL to reflect the new chat ID
      navigate(`/chat/${encrypt(newChat.id)}`);
      return newChat;
    } catch (error) {
      console.error('Error creating chat:', error);
      return null;
    }
  };

  const sendMessage = async (text) => {
    if (!isLoggedIn || !user) {
      console.error('User not logged in');
      return;
    }

    try {
      let activeChat = currentConversationIndex !== null ? conversations[currentConversationIndex] : null;

      // Handle scenario where no active chat exists
      if (!activeChat) {
        activeChat = await startNewChat();
        if (!activeChat) {
          console.error('Failed to create a new chat');
          setLoading(false);
          return;
        }
        setCurrentConversationIndex(0); // New chat is added at the top
      }

      const chatId = activeChat.id;

      // Set the active response chat ID to track which chat is receiving a response
      setActiveResponseChatId(chatId);

      // Add user's message to local state
      setConversations((prev) =>
        prev.map((chat) =>
          chat.id === chatId
            ? { ...chat, messages: [...chat.messages, { sender: 'user', message: text }] }
            : chat
        )
      );

      setLoading(true);

      // Initialize AI response
      let aiMessage = '';
      let isFirstChunk = true;

      // Stream AI response
      const reader = await fetchAIResponse(chatId, text, isWebSearch);
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        aiMessage += decoder.decode(value, { stream: true });

        // Update AI message in local state
        setConversations((prev) =>
          prev.map((chat) =>
            chat.id === chatId
              ? {
                ...chat,
                messages: isFirstChunk
                  ? [...chat.messages, { sender: 'ai', message: aiMessage }] // Add first AI message
                  : chat.messages.map((msg, index) =>
                    index === chat.messages.length - 1 && msg.sender === 'ai'
                      ? { ...msg, message: aiMessage } // Update last AI message
                      : msg
                  ),
              }
              : chat
          )
        );

        isFirstChunk = false; // Mark that we've handled the first chunk
        // In your sendMessage function, after fully receiving the AI message:
        setLastBotResponse(aiMessage);
      }

      setLoading(() => false); // Set streaming status to false immediately after response

      setActiveResponseChatId(null); // Reset the active response chat ID

      //checking remaining tokens from AI
      const result = await checkRemainingTokens();
      setcheckRemainingAITokens(result.can_generate);
      console.log('Remaining tokens:', checkRemainingAITokens);

      // Rename chat if it's the first message
      const isFirstMessage = activeChat.messages.length === 0;
      if (isFirstMessage) {
        const titleResponse = await suggestTitle(chatId, text);
        const newTitle = titleResponse.title || 'Untitled Chat';
        await renameChatAPI(chatId, newTitle);
        setConversations((prev) =>
          prev.map((chat) =>
            chat.id === chatId ? { ...chat, title: newTitle } : chat
          )
        );
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
      setActiveResponseChatId(null); // Make sure to reset on error too
    }
  };

  // Handle the model selection change
  const handleModelChange = (newModel, newModelText) => {
    if (newModel !== model) {
      const updatedConversations = [...conversations];
      updatedConversations[currentConversationIndex].messages.push({
        sender: 'system',
        message: `You are using the ${newModelText} model.`,
      });
      setConversations(updatedConversations);
      setModel(newModel);
    }
  };

  // Handle conversation history click
  const handleHistoryClick = async (index) => {
    setCurrentConversationIndex(index);
    const chatId = conversations[index].id;
    // Update the URL to show the selected chat's ID
    navigate(`/chat/${encrypt(chatId)}`);
    setChatId(chatId);
    const response = await fetchChatModel(chatId);
    const modelId = response.model_id;
    setModel(modelId);

    try {
      const response = await fetchChatMessages(chatId);
      const updatedConversations = [...conversations];
      updatedConversations[index].messages = response.messages;
      setConversations(updatedConversations);
    } catch (error) {
      console.error('Error fetching chat messages:', error);
    }
  };

  const renameChat = async (chatId, newTitle) => {
    try {
      await renameChatAPI(chatId, newTitle);

      setConversations((prev) =>
        prev.map((chat) =>
          chat.id === chatId ? { ...chat, title: newTitle } : chat
        )
      );
    } catch (error) {
      console.error('Error renaming chat:', error);
    }
  };

  const deleteChat = async (chatId) => {
    try {
      await deleteChatAPI(chatId);
      fetchChats();
      setCurrentConversationIndex(null);
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  const addFileMessage = (fileMessage) => {
    if (!isLoggedIn || !user) {
      console.error('User not logged in');
      return;
    }

    const activeChat = currentConversationIndex !== null ? conversations[currentConversationIndex] : null;
    if (!activeChat) {
      console.error('No active chat');
      return;
    }

    // Update conversations state with the file message
    setConversations((prev) =>
      prev.map((chat) =>
        chat.id === activeChat.id
          ? { ...chat, messages: [...chat.messages, fileMessage] }
          : chat
      )
    );
  };

  const handleModelSelect = async (newModel, newModelText) => {
    // If no active chat, create a new one with the selected model
    if (currentConversationIndex === null) {
      const newChat = await startNewChatWithModel(newModel, newModelText);
      if (newChat) {
        setModel(newModel);
      }
    } else {
      // If there is an active chat, just change the model
      handleModelChange(newModel, newModelText);
    }
  };

  const startNewChatWithModel = async (modelId, modelName) => {
    if (!isLoggedIn || !user) {
      console.error('User not logged in');
      return null;
    }

    try {
      const response = await createChat(user, `Conversation ${conversations.length + 1}`, modelId);
      const newChat = {
        id: response.chat_id,
        title: `Conversation ${conversations.length + 1}`,
        messages: [{ sender: 'system', message: `You are using the ${modelName} model.` }],
      };
      setChatId(newChat.id);
      setConversations([newChat, ...conversations]);
      setCurrentConversationIndex(0);
      navigate(`/chat/${encrypt(newChat.id)}`);
      return newChat;
    } catch (error) {
      console.error('Error creating chat:', error);
      return null;
    }
  };
  
  return (
    <div className={`app ${isOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Sidebar
        isOpen={isOpen}
        toggleSidebar={toggleSidebar}
        startNewChat={startNewChat}
        chatHistory={conversations}
        handleHistoryClick={handleHistoryClick}
        currentConversationIndex={currentConversationIndex}
        renameChat={renameChat}
        deleteChat={deleteChat}
        refreshChats={fetchChats}
        setGlobalUserName={setGlobalUserName}
        setGlobalEmail={setGlobalEmail}
        isDarkTheme={isDarkTheme}
      />
      <div className="main-content">
        <Navbar
          isOpen={isOpen}
          toggleSidebar={toggleSidebar}
          startNewChat={startNewChat}
          model={model}
          setModel={handleModelChange}
          chatId={chatId}
          userName={globalUserName}
          Email={globalEmail}
        />
        <CustomToaster isDarkTheme={isDarkTheme}/>
        <Chatbox messages={conversations[currentConversationIndex]?.messages || []} selectedModel={model} loading={loading} isDarkTheme={isDarkTheme} currentConversationId={chatId} activeConversationId={activeResponseChatId} onModelSelect={handleModelSelect}/>
        <ChatInput onSend={sendMessage} loading={loading} isDarkTheme={isDarkTheme} lastBotResponse={lastBotResponse} chat_id={chatId} addFileMessage={addFileMessage} checkRemainingAITokens={checkRemainingAITokens} isWebSearch={isWebSearch} setIsWebSearch={setIsWebSearch} />
      </div>
    </div>
  );
}

export default ChatPage;
